import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-for-anxiety-at-night",
  title: "Bible verses for anxiety at night: 10 passages for 2am",
  h1: "Bible verses for anxiety at night",
  description:
    "Ten passages for the hours when the worry is loudest, with a note on each — including why so many of them are about sleep specifically rather than about fear in general.",
  targets: [
    "bible verses for anxiety at night",
    "scripture for sleepless nights",
    "verses to pray when you cannot sleep",
  ],
  published: "2026-08-26",
  standfirst:
    "Night anxiety is its own thing, and scripture treats it as its own thing — a surprising number of these passages are about lying down specifically.",
  takeaways: [
    "Several of the best-known passages on fear are explicitly about the night: Psalm 4:8, Psalm 3:5 and Proverbs 3:24 all describe lying down and sleeping rather than fear in the abstract.",
    "Philippians 4:6 does not say stop worrying — it says turn the worry into a request, which is a different instruction and a possible one at three in the morning.",
    "Matthew 6:34 limits the scope rather than the feeling: tomorrow is not today's business, and night worry is almost entirely about tomorrow.",
    "If the sleeplessness is chronic rather than occasional, these passages are companions to treatment and not a substitute for it.",
  ],
  intro: [
    {
      type: "p",
      text: "Anxiety at night is not simply daytime anxiety happening later. It has its own machinery: the body is tired, the reasoning is slower, nothing can be done about anything until morning, and the mind reliably reaches for the one subject that cannot be resolved. Anyone who has lain awake at three knows that the thought arriving then is rarely the thought that seemed important at six.",
    },
    {
      type: "p",
      text: "Scripture appears to know this. A striking number of the passages people reach for are about lying down specifically — the moment, the posture, the sleep itself — rather than about fear as a general condition. The notes below say what each one is actually addressing, because a verse aimed at a different problem is a verse that will not help at two in the morning.",
    },
    {
      type: "note",
      text: "If you are awake most nights, or the worry comes with low mood or a racing heart, that is worth a conversation with a doctor. These passages sit alongside that, not instead of it.",
    },
  ],
  verses: [
    {
      verse: {
        text: "In peace I will both lay myself down and sleep, for you alone, Yahweh, make me live in safety.",
        ref: "Psalm 4:8",
      },
      note: "Written by someone with real enemies rather than by someone with a busy mind, which is what makes it usable: the claim is not that the danger is imaginary but that the watching is not his job tonight. Sleep here is an act of handing over the shift.",
    },
    {
      verse: {
        text: "I laid myself down and slept. I awakened; for Yahweh sustains me.",
        ref: "Psalm 3:5",
      },
      note: "The past tense is the whole comfort. It is a report of a night that was survived rather than a promise about one that has not started, and at three in the morning a report is easier to hold than a promise. This psalm is traditionally set during Absalom's rebellion — about as bad a night as a person can have.",
    },
    {
      verse: {
        text: "When you lie down, you will not be afraid. Yes, you will lie down, and your sleep will be sweet.",
        ref: "Proverbs 3:24",
      },
      note: "Part of a longer passage about wisdom being kept in view, and the sequence matters: the sleep is the consequence of something done earlier in the day, not a technique applied at bedtime. It is the closest thing scripture has to an argument that night peace is built in daylight.",
    },
    {
      verse: {
        text: "In nothing be anxious, but in everything, by prayer and petition with thanksgiving, let your requests be made known to God.",
        ref: "Philippians 4:6",
      },
      note: "The instruction is often heard as stop worrying, which is not an action anyone can take. What it actually says is where to put it: turn the anxiety into a specific request. That is doable at three in the morning, and unlike suppression it gives the mind something to do.",
    },
    {
      verse: {
        text: "And the peace of God, which surpasses all understanding, will guard your hearts and your thoughts in Christ Jesus.",
        ref: "Philippians 4:7",
      },
      note: "The following verse, and it should never be quoted apart from the one above. The peace is described as a guard — something posted around the thinking rather than a feeling arriving inside it. Notably, it surpasses understanding, which means it does not require the situation to have been explained first.",
    },
    {
      verse: {
        text: "Therefore don't be anxious for tomorrow, for tomorrow will be anxious for itself. Each day's own evil is sufficient.",
        ref: "Matthew 6:34",
      },
      note: "The most practical verse in the list for night specifically, because night worry is almost always about tomorrow. It does not say the concern is unreal. It says it belongs to a day that has not started, and it says today has already had enough — which is a remarkably unsentimental sentence.",
    },
    {
      verse: {
        text: "Casting all your worries on him, because he cares for you.",
        ref: "1 Peter 5:7",
      },
      note: "The verb is forceful — throwing, not placing — and the reason given is not that the worry is trivial. It is that the one receiving it is interested. That distinction is the difference between being told to be quiet and being told somebody is listening.",
    },
    {
      verse: {
        text: "When I am afraid, I will put my trust in you.",
        ref: "Psalm 56:3",
      },
      note: "Six words that assume the fear will happen. There is no attempt here to reach a state where fear is absent; the trust is what is done inside it. For anyone who has concluded that being afraid at night is itself a failure, this is the correction.",
    },
    {
      verse: {
        text: "It is vain for you to rise up early, to stay up late, eating the bread of toil; for he gives sleep to his loved ones.",
        ref: "Psalm 127:2",
      },
      note: "An unusually direct verse about overwork, and it names the specific pattern: up early, up late, anxious about provision. The claim is that the extra hours are not producing what they promise. Anyone whose late nights are half work and half scrolling will recognise the shape of the bargain being described.",
    },
    {
      verse: {
        text: "Come to me, all you who labor and are heavily burdened, and I will give you rest.",
        ref: "Matthew 11:28",
      },
      note: "Rest offered to people who are carrying something, not to people who are idle. The invitation is addressed precisely to the state that keeps someone awake — and the verses following it describe a yoke, meaning the rest offered is not the absence of work but a different way of carrying it.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "using-these-at-3am",
      text: "How to use these at three in the morning",
    },
    {
      type: "p",
      text: "Choose one, in daylight, and learn it. That is the only version of this that works, because at three in the morning nobody is picking a passage from a list — and reaching for the phone to find one is how a bad night becomes a worse one. Two of these are short enough to memorise in a minute: Psalm 56:3 and Matthew 6:34.",
    },
    {
      type: "p",
      text: "The second thing is to give the worry somewhere to go rather than trying to stop it. Philippians 4:6 is an instruction to convert it: say the specific request out loud, in one sentence, and then say it again when it comes back. It will come back. The point is not to win the argument but to have somewhere to put it each time.",
    },
    {
      type: "p",
      text: "And if the night starts on a screen — which for most people now it does — the passages are working against a headwind. {The-evening-piece} covers what actually changes that, and it is mostly about the hour before bed rather than the hour after it.",
      links: { "The-evening-piece": "/blog/how-to-stop-scrolling-at-night" },
    },
    {
      type: "h2",
      id: "a-night-prayer",
      text: "A night prayer from these passages",
    },
    {
      type: "p",
      text: "Lord, I am afraid, and I am putting my trust in you. Tomorrow is not mine tonight. I am handing you the thing I cannot fix at this hour, and I am asking for the sleep you give to those you love. Guard my thoughts, since I cannot. Amen.",
    },
  ],
  faq: [
    {
      q: "Does the Bible say anything about insomnia specifically?",
      a: "Not as a medical condition, but several passages deal with the experience directly — Psalm 4:8, Psalm 3:5, Proverbs 3:24 and Psalm 127:2 are all about lying down, sleeping, or failing to. Psalm 77 is worth reading in full: it was written by somebody who could not sleep and did not pretend otherwise.",
    },
    {
      q: "What if praying makes the worry louder?",
      a: "That is common at night, and it usually means the prayer has turned into rehearsing the problem. Philippians 4:6 is specific about the form: a request, said once, with thanks attached — not a review. If it keeps turning into one, a psalm you have memorised gives the mind fixed words instead of your own.",
    },
    {
      q: "Which translation is used here?",
      a: "The World English Bible, which is in the public domain. It renders the divine name as Yahweh where other translations print LORD.",
    },
  ],
  related: [
    "how-to-stop-scrolling-at-night",
    "digital-sabbath",
    "phone-addiction-bible-verses",
  ],
  relatedTopics: ["bible-verses-about-rest", "bible-verses-about-time"],
};
